import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Circle, Loader2, Paperclip, Upload, FileText, Download, Undo2 } from "lucide-react";
import api from "../lib/api";
import { formatShort, formatApiError } from "../lib/format";

export default function ProcessTimeline({ processId, readOnly = false, onClose, onChanged }) {
  const [proc, setProc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyStep, setBusyStep] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/processes/${processId}`);
      setProc(data);
      const drafts = {};
      (data.steps || []).forEach(s => { drafts[s.id] = s.notes || ""; });
      setNotesDraft(drafts);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (processId) load(); }, [processId]);

  const saveStep = async (step, status) => {
    setBusyStep(step.id);
    try {
      const { data } = await api.patch(`/processes/${processId}/steps/${step.id}`, {
        status, notes: notesDraft[step.id] || "",
      });
      setProc(data);
      toast.success(status === "done" ? "Etapa concluída" : status === "in_progress" ? "Etapa em andamento" : "Etapa reaberta");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyStep(null); }
  };

  const saveNotes = async (step) => {
    setBusyStep(step.id);
    try {
      const { data } = await api.patch(`/processes/${processId}/steps/${step.id}`, {
        notes: notesDraft[step.id] || "",
      });
      setProc(data);
      toast.success("Notas salvas");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyStep(null); }
  };

  const uploadAttachment = async (step, file) => {
    if (!file) return;
    setBusyStep(step.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "Comprovante");
      const { data } = await api.post(`/processes/${processId}/steps/${step.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProc(data);
      toast.success("Comprovante anexado");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyStep(null); if (fileRefs.current[step.id]) fileRefs.current[step.id].value = ""; }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!processId) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal timeline-modal" data-testid="process-timeline" onClick={e => e.stopPropagation()}>
        <button className="modal-close" data-testid="close-timeline" onClick={onClose}><X size={18}/></button>
        {loading || !proc ? (
          <div className="timeline-loading"><Loader2 className="spin" size={22}/> Carregando timeline...</div>
        ) : (
          <>
            <span className="eyebrow">TIMELINE DO PROCESSO</span>
            <h2>{proc.kind}</h2>
            <p className="timeline-sub">{proc.client_name} · {proc.status} · {proc.progress}% concluído</p>

            <div className="timeline-progress-wrap">
              <div className="timeline-progress"><i style={{ width: `${proc.progress || 0}%` }}/></div>
            </div>

            <div className="timeline-list">
              {(proc.steps || []).map((step, idx) => {
                const isDone = step.status === "done";
                const isCurr = step.status === "in_progress";
                const status = isDone ? "done" : isCurr ? "current" : "pending";
                return (
                  <div key={step.id} className={`timeline-step ${status}`} data-testid={`timeline-step-${idx}`}>
                    <div className="timeline-marker">
                      {isDone ? <CheckCircle2 size={20}/> : isCurr ? <Loader2 size={18} className="spin"/> : <Circle size={18}/>}
                      {idx < (proc.steps.length - 1) && <div className="timeline-line"/>}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-head">
                        <b>{step.label}</b>
                        {step.completed_at && <time>{formatShort(step.completed_at.slice(0,10))}</time>}
                      </div>

                      {readOnly ? (
                        <>
                          {step.notes && <p className="timeline-notes">{step.notes}</p>}
                          {(step.attachment_docs?.length || 0) > 0 && (
                            <div className="timeline-attachments">
                              {step.attachment_docs.map(d => (
                                <button key={d.id} className="timeline-attach-chip" data-testid={`portal-attach-${d.id}`} onClick={()=>downloadDoc(d)}>
                                  <FileText size={13}/> {d.filename} <Download size={12}/>
                                </button>
                              ))}
                            </div>
                          )}
                          {!step.notes && !(step.attachment_docs?.length) && !isDone && (
                            <span className="timeline-empty-inline">Aguardando execução</span>
                          )}
                        </>
                      ) : (
                        <>
                          <textarea
                            data-testid={`timeline-notes-${idx}`}
                            rows={2}
                            className="timeline-textarea"
                            placeholder="Notas da etapa (visíveis ao cliente)..."
                            value={notesDraft[step.id] ?? ""}
                            onChange={e => setNotesDraft({ ...notesDraft, [step.id]: e.target.value })}
                          />
                          {(step.attachment_docs?.length || 0) > 0 && (
                            <div className="timeline-attachments">
                              {step.attachment_docs.map(d => (
                                <button key={d.id} className="timeline-attach-chip" data-testid={`attach-chip-${d.id}`} onClick={()=>downloadDoc(d)}>
                                  <FileText size={13}/> {d.filename} <Download size={12}/>
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="timeline-actions">
                            <label className="ghost-button" data-testid={`timeline-upload-label-${idx}`}>
                              <Paperclip size={14}/> Anexar comprovante
                              <input
                                data-testid={`timeline-file-${idx}`}
                                ref={el => fileRefs.current[step.id] = el}
                                type="file"
                                style={{ display: "none" }}
                                accept=".pdf,image/*"
                                onChange={e => uploadAttachment(step, e.target.files?.[0])}
                              />
                            </label>
                            <button className="ghost-button" data-testid={`timeline-save-notes-${idx}`} onClick={() => saveNotes(step)} disabled={busyStep === step.id}>
                              Salvar notas
                            </button>
                            {isDone ? (
                              <button className="ghost-button warning" data-testid={`timeline-reopen-${idx}`} onClick={() => saveStep(step, "pending")} disabled={busyStep === step.id}>
                                <Undo2 size={14}/> Reabrir
                              </button>
                            ) : (
                              <button className="primary-button small" data-testid={`timeline-complete-${idx}`} onClick={() => saveStep(step, "done")} disabled={busyStep === step.id}>
                                <CheckCircle2 size={14}/> {busyStep === step.id ? "Salvando..." : "Concluir etapa"}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
